/* eslint-disable react-refresh/only-export-components -- column definitions + their cell renderers */
import type { ColumnDef } from '@tanstack/react-table';
import { WorkModel } from '@clawhire/shared';
import { EMPTY, formatDate, formatRate, truncate } from '@/lib/format';
import { EnumBadge } from '@/components/data/EnumBadge';
import { FlagBadges, PortalBadge, RateLine, StageBadge } from '../components/PositionBadges';
import type { PositionListItem } from '../types';
import { GoDecisionCell, GoReasonCell, type PatchPosition } from './GoDecisionCell';

/** Column ids referenced by saved views (`columns: [...]`). */
export const ALL_COLUMN_IDS = [
  'requestNo',
  'role',
  'stage',
  'portalStatus',
  'flags',
  'goDecision',
  'goReason',
  'startDate',
  'endDate',
  'submissionDeadline',
  'clientRateEur',
  'supplierCeiling',
  'submissionsCount',
  'intermediary',
  'owner',
  'workModel',
  'location',
  'department',
  'restrictions',
  'createdAt',
] as const;

export type ColumnId = (typeof ALL_COLUMN_IDS)[number];

/** Visible when the view has no column list. */
export const DEFAULT_COLUMNS: ColumnId[] = [
  'requestNo',
  'role',
  'stage',
  'portalStatus',
  'flags',
  'goDecision',
  'startDate',
  'clientRateEur',
  'submissionsCount',
  'intermediary',
  'owner',
];

/** Always visible; cannot be hidden from the column chooser. */
export const PINNED_COLUMNS: ColumnId[] = ['requestNo', 'role'];

export function buildColumns(opts: {
  canWrite: boolean;
  onPatch: PatchPosition;
}): ColumnDef<PositionListItem, unknown>[] {
  const { canWrite, onPatch } = opts;
  return [
    {
      id: 'requestNo',
      accessorKey: 'requestNo',
      header: 'Request',
      enableHiding: false,
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.requestNo}</span>,
      size: 110,
    },
    {
      id: 'role',
      accessorKey: 'role',
      header: 'Role',
      enableHiding: false,
      cell: ({ row }) => (
        <span className="font-medium" title={row.original.role}>
          {truncate(row.original.role, 60)}
        </span>
      ),
      size: 260,
    },
    {
      id: 'stage',
      accessorKey: 'stage',
      header: 'Stage',
      cell: ({ row }) => <StageBadge stage={row.original.stage} />,
    },
    {
      id: 'portalStatus',
      accessorKey: 'portalStatusDetail',
      header: 'Portal',
      cell: ({ row }) => <PortalBadge position={row.original} />,
    },
    {
      id: 'flags',
      accessorFn: (p) => (p.computed.isUrgent ? 2 : 0) + (p.computed.onSiteBlocked ? 1 : 0),
      header: 'Flags',
      cell: ({ row }) => (
        <span className="inline-flex flex-wrap gap-1">
          <FlagBadges computed={row.original.computed} size="sm" />
        </span>
      ),
    },
    {
      id: 'goDecision',
      accessorKey: 'goDecision',
      header: 'Go decision',
      cell: ({ row }) => <GoDecisionCell position={row.original} canEdit={canWrite} onPatch={onPatch} />,
    },
    {
      id: 'goReason',
      accessorKey: 'goReason',
      header: 'Go reason',
      cell: ({ row }) => <GoReasonCell position={row.original} canEdit={canWrite} onPatch={onPatch} />,
    },
    {
      id: 'startDate',
      accessorKey: 'startDate',
      header: 'Start',
      cell: ({ row }) => <DateCell value={row.original.startDate} />,
    },
    {
      id: 'endDate',
      accessorKey: 'endDate',
      header: 'End',
      cell: ({ row }) => <DateCell value={row.original.endDate} />,
    },
    {
      id: 'submissionDeadline',
      accessorKey: 'submissionDeadline',
      header: 'Deadline',
      cell: ({ row }) => <DateCell value={row.original.submissionDeadline} />,
    },
    {
      id: 'clientRateEur',
      accessorFn: (p) => p.rates.clientRateH ?? -1,
      header: 'Client rate',
      meta: { align: 'right' },
      cell: ({ row }) => <RateLine position={row.original} rates={row.original.rates} />,
    },
    {
      id: 'supplierCeiling',
      accessorFn: (p) => p.rates.supplierCeilingDisplay ?? -1,
      header: 'Supplier ceiling',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span
          className={row.original.rates.supplierCeilingDisplay === null ? 'text-muted-foreground' : undefined}
        >
          {formatRate(row.original.rates.supplierCeilingDisplay, 'HOUR')}
        </span>
      ),
    },
    {
      id: 'submissionsCount',
      accessorFn: (p) => p.computed.submissionsCount,
      header: 'Submissions',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const c = row.original.computed;
        return (
          <span>
            {c.submissionsCount}
            {c.submittedCount > 0 ? (
              <span className="text-muted-foreground"> ({c.submittedCount} submitted)</span>
            ) : null}
          </span>
        );
      },
    },
    {
      id: 'intermediary',
      accessorFn: (p) => p.intermediary?.name ?? '',
      header: 'Intermediary',
      cell: ({ row }) =>
        row.original.intermediary ? (
          <span>{row.original.intermediary.name}</span>
        ) : (
          <span className="text-muted-foreground">{EMPTY}</span>
        ),
    },
    {
      id: 'owner',
      accessorFn: (p) => p.owner?.name ?? '',
      header: 'Owner',
      cell: ({ row }) =>
        row.original.owner ? (
          <span title={row.original.owner.email}>{row.original.owner.name}</span>
        ) : (
          <span className="text-muted-foreground">{EMPTY}</span>
        ),
    },
    {
      id: 'workModel',
      accessorKey: 'workModel',
      header: 'Work model',
      cell: ({ row }) =>
        row.original.workModel ? (
          <EnumBadge def={WorkModel} value={row.original.workModel} size="sm" />
        ) : (
          <span className="text-muted-foreground">{EMPTY}</span>
        ),
    },
    {
      id: 'location',
      accessorKey: 'location',
      header: 'Location',
      cell: ({ row }) => <TextCell value={row.original.location} />,
    },
    {
      id: 'department',
      accessorKey: 'department',
      header: 'Department',
      cell: ({ row }) => <TextCell value={row.original.department} />,
    },
    {
      id: 'restrictions',
      accessorKey: 'restrictions',
      header: 'Restrictions',
      cell: ({ row }) => <TextCell value={row.original.restrictions} max={50} />,
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => <DateCell value={row.original.createdAt} />,
    },
  ];
}

function DateCell({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-muted-foreground">{EMPTY}</span>;
  return (
    <time dateTime={value} className="tabular-nums">
      {formatDate(value)}
    </time>
  );
}

function TextCell({ value, max = 40 }: { value: string | null | undefined; max?: number }) {
  if (!value) return <span className="text-muted-foreground">{EMPTY}</span>;
  return <span title={value}>{truncate(value, max)}</span>;
}
